import { useEffect, useState } from "react";
import { Box, Paper, Title, TextInput, PasswordInput, Button, Divider, Anchor, Flex } from "@mantine/core";
import { IconMail, IconLock, IconShield, IconWorld, IconUsers } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import trpc, { saveToken } from "../../shared/utils/trpc";
import {
  TWO_FACTOR_DEVICE_TOKEN_KEY,
  TWO_FACTOR_RETURN_PATH_KEY,
  TWO_FACTOR_TEMP_TOKEN_KEY,
} from "@/two-factor/constants";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import { successNotification } from "../../shared/utils/successNotification";
import { useUser } from "@/context/UserContext";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";
function LoginPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { refetchUser } = useUser();

  const token = Cookies.get("token");

  useEffect(() => {
    if (!token) return;

    try {
      const decoded = jwtDecode<{ role: string; exp: number }>(token);

      if (decoded.exp > Date.now() / 1000) {
        if (decoded.role === "ADMIN") {
          navigate("/admin/companies", { replace: true });
        } else {
          navigate("/my-discovery", { replace: true });
        }
      } else {
        Cookies.remove("token");
      }
    } catch {
      Cookies.remove("token");
    }
  }, [token, navigate]);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 8;
  const isFormValid = email.trim() !== "" && password.trim() !== "" && isValidEmail(email) && isPasswordValid;

  const handleLogin = async () => {
    if (!isFormValid) return;
    setLoading(true);
    try {
      const deviceTok = localStorage.getItem(TWO_FACTOR_DEVICE_TOKEN_KEY);
      const data = await trpc.auth.login.mutate({
        email,
        password,
        ...(deviceTok ? { trustedDeviceToken: deviceTok } : {}),
      });
      if ("requires2FA" in data && data.requires2FA) {
        sessionStorage.setItem(TWO_FACTOR_TEMP_TOKEN_KEY, data.tempToken);
        sessionStorage.setItem(TWO_FACTOR_RETURN_PATH_KEY, "/my-discovery");
        navigate("/login/2fa", { replace: true });
        return;
      }
      if (!("token" in data)) return;
      saveToken(data.token);
      await refetchUser();
      successNotification({ title: "Login Successful!", message: "You have successfully logged in." });
      navigate("/my-discovery", { replace: true });
    } catch (err: unknown) {
      const code =
        err && typeof err === "object" && "data" in err
          ? (err as { data?: { code?: string } }).data?.code
          : undefined;
      const message =
        err && typeof err === "object" && "message" in err ? String((err as { message: unknown }).message) : "";
      if (code === "FORBIDDEN" && message.includes("verify your email")) {
        try {
          await trpc.auth.resendVerification.mutate({ email: email.trim() });
          successNotification({
            title: "Verification code sent",
            message: "Please enter the OTP sent to your email to continue.",
          });
        } catch (sendOtpErr) {
          handleTrpcError(sendOtpErr);
        }
        navigate(`/resend-email?email=${encodeURIComponent(email.trim())}`, { replace: true });
        return;
      }
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ height: "100vh", display: "flex", overflow: "hidden" }}>
      {/* ── Left panel ── */}
      <Box
        style={{
          flex: "0 0 50%",
          position: "relative",
          overflow: "hidden",
          background: `
            linear-gradient(90deg, rgba(100, 200, 255, 0.15) 2px, transparent 2px),
            linear-gradient(rgba(100, 200, 255, 0.15) 2px, transparent 2px),
            linear-gradient(90deg, rgba(100, 200, 255, 0.08) 1px, transparent 1px),
            linear-gradient(rgba(100, 200, 255, 0.08) 1px, transparent 1px),
            linear-gradient(135deg, #0a2e5c 0%, #0f3a66 50%, #1a4d7a 100%)
          `,
          backgroundSize: "200px 200px, 200px 200px, 40px 40px, 40px 40px, 100% 100%",
        }}
        visibleFrom="md"
      >

        {/* Text overlay */}
        <Box
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "60px 56px",
          }}
        >
          <div>
            <Flex
              align="center"
              gap={8}
              className="inline-flex px-4 py-3 text-white text-sm select-none"
              style={{ 
                display: "inline-flex",
                borderRadius: "9999px",
                background: "rgba(255, 255, 255, .08)",
                border: "1px solid rgba(255, 255, 255, .2)"
              }}
            >
              <IconShield size={16} className="text-[#fbf707]" />
              Verification-First Global Expansion
            </Flex>
          </div>
          <p style={{ fontSize: 36, fontWeight: 700, color: "#ffffff", marginBottom: 20, marginTop: 16 }}>
            Connect with <span className="text-[oklch(87.9%_0.169_91.605)]">Global Collaboration</span> Partners
          </p>

          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.8)", marginBottom: 48, marginTop: 0 }}>
            Join a trusted, verified network of international businesses. Discover opportunities,<span className="text-[oklch(87.9%_0.169_91.605)]" > build partnerships</span>,
            and grow your global reach.
          </p>

          <Flex direction="column" gap={20}>
            {[
              { icon: <IconShield size={18} />, text: "Verified business profiles you can trust" },
              { icon: <IconWorld size={18} />, text: "Access partners across 50+ countries" },
              { icon: <IconUsers size={18} />, text: "Collaborate securely with built-in NDA tools" },
            ].map(({ icon, text }) => (
              <Flex key={text} align="center" gap={14}>
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: "rgba(251, 247, 7, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "oklch(87.9% 0.169 91.605)",
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <span style={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }}>{text}</span>
              </Flex>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* ── Right panel ── */}
      <Box
        style={{
          flex: 1,
          background: "var(--mantine-color-white)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 32px",
          overflowY: "auto",
        }}
      >
        <Box style={{ width: "100%", maxWidth: 360 }}>
          {/* Logo + subtitle */}
          <Flex direction="column" align="center" mb={32}>
            <img src="/gtLogo.svg" alt="GTsetu" style={{ height: 100, width: "auto", marginBottom: 8 }} />
          </Flex>

          {/* Form card */}
          <Paper p="xl" radius="lg" bg="#f7f9ff" style={{ width: "100%", border: "1px solid #04305c" }}>
            <Title order={3} mb="lg">
              Log In
            </Title>

            <TextInput
              label="Email Address"
              placeholder="you@company.com"
              required
              mb="md"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              leftSection={<IconMail size={16} color="var(--mantine-color-slate-4)" />}
              error={email && !isValidEmail(email) ? "Please enter a valid email address" : null}
            />

            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              required
              mb="md"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              leftSection={<IconLock size={16} color="var(--mantine-color-slate-4)" />}
              error={password && !isPasswordValid ? "Password must be at least 8 characters" : null}
            />

            <Flex justify="flex-end" mt={6} mb="md">
              <Anchor size="sm" component={Link} to="/forgot-password">
                Forgot password?
              </Anchor>
            </Flex>

            <Button fullWidth mb="md" onClick={handleLogin} loading={loading} disabled={!isFormValid || loading}>
              Log In
            </Button>

            <Divider label="New to GTsetu?" labelPosition="center" my="md" />

            <Button fullWidth variant="outline" component={Link} to="/signup">
              Create Account
            </Button>
          </Paper>

          {/* Back to home */}
          <Flex justify="center" mt="lg">
            <Anchor href="https://gtsetu.com/" size="sm" c="slate.5">
              ← Back to Home
            </Anchor>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

export default LoginPage;
